import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { FileMinusIcon, XIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { SearchInput } from '@ems/ui/patterns/search-input'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDateTime, formatMoney } from '@ems/ui/lib/format'

import { FilteredEmpty } from '#admin/components/list-filter.tsx'
import { OptionSelect } from '#admin/components/option-select.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { CreditNotesSkeleton } from '#admin/features/billing/billing-skeletons.tsx'
import {
  matchesCreditNote,
  parseCreditNoteSearch,
} from '#admin/features/billing/credit-note-filter.ts'
import {
  CREDIT_NOTE_STATUSES,
  creditNoteTone,
  type CreditNote,
} from '#admin/features/billing/credit-notes-api.ts'
import { useIssueCreditNote, useVoidCreditNote } from '#admin/features/billing/mutations.ts'
import { creditNotesQuery } from '#admin/features/billing/queries.ts'
import { label } from '#admin/features/registrations/api.ts'
import { NoteDialog } from '#admin/features/registrations/form-dialog.tsx'
import { useLiveSearch } from '#admin/lib/use-live-search.ts'

/**
 * `/billing/credit-notes` — every credit note in the edition. They are drafted from the invoice
 * they correct; here they are issued, voided, and found again. The search and the status filter
 * live in the URL.
 */
export const Route = createFileRoute('/_authenticated/billing/credit-notes')({
  pendingComponent: CreditNotesSkeleton,
  validateSearch: parseCreditNoteSearch,
  staticData: { crumb: { parents: [{ label: 'Billing' }], label: 'Credit notes' } },
  loader: ({ context }) => context.queryClient.ensureQueryData(creditNotesQuery()),
  component: CreditNotesPage,
})

/** `all` rather than `''`: Radix reserves the empty string for "nothing chosen". */
const ANY = 'all'

function CreditNotesPage() {
  const { data } = useSuspenseQuery(creditNotesQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'billing.manage')
  const [dialog, setDialog] = useState<{ kind: 'issue' | 'void'; note: CreditNote } | null>(null)
  const close = () => setDialog(null)
  const issue = useIssueCreditNote()
  const voidIt = useVoidCreditNote()

  // Filtered in the browser: the list is already here in full.
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [draft, setDraft] = useLiveSearch(
    search.q ?? '',
    (term) =>
      void navigate({
        search: (prev) => parseCreditNoteSearch({ ...prev, q: term }),
        replace: true,
      }),
  )
  const shown = data.items.filter((note) => matchesCreditNote(note, search))
  const filtered = search.q !== undefined || search.status !== undefined
  // One page at a time. A new search or status starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(
    `${search.status ?? ''}|${search.q ?? ''}`,
  )
  const paged = pageSlice(shown, page, pageSize)
  const clear = () => {
    setDraft('')
    void navigate({ search: {} })
  }

  const columns = useMemo<ColumnDef<CreditNote, never>[]>(
    () => [
      {
        accessorKey: 'number',
        header: 'Number',
        meta: { primary: true },
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.number ?? 'draft'}</span>
        ),
      },
      {
        id: 'invoice',
        header: 'Invoice',
        cell: ({ row }) => (
          <span className="flex min-w-0 flex-col">
            <Link
              to="/billing/invoices/$invoiceId"
              params={{ invoiceId: row.original.invoiceId }}
              className="font-mono text-xs underline"
            >
              {row.original.invoiceNumber ?? 'draft'}
            </Link>
            {row.original.billToName === null ? null : (
              <span className="text-muted-foreground truncate text-xs">
                {row.original.billToName}
              </span>
            )}
          </span>
        ),
      },
      { accessorKey: 'reason', header: 'Reason', meta: { wrap: true } },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={creditNoteTone(row.original.status)}>
            {row.original.status}
          </StatusBadge>
        ),
      },
      {
        id: 'issuedAt',
        header: 'Issued',
        meta: { hideOnCard: true },
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.issuedAt === null ? '—' : formatDateTime(row.original.issuedAt)}
          </span>
        ),
      },
      {
        id: 'total',
        header: 'Amount',
        meta: { align: 'end' },
        cell: ({ row }) => formatMoney(row.original.total, row.original.currency),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: 'end', shrink: true },
        cell: ({ row }) => {
          const note = row.original
          if (!canManage || note.status === 'voided') return null
          return note.status === 'draft' ? (
            <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'issue', note })}>
              Issue
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setDialog({ kind: 'void', note })}>
              Void
            </Button>
          )
        },
      },
    ],
    [canManage],
  )

  const name = dialog?.note.number ?? 'this credit note'

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Credit notes"
        description="Corrections to invoices money has already moved against. Draft one from the invoice it corrects."
      />

      {canManage ? null : <ReadOnlyNotice area="credit notes" />}

      {data.items.length === 0 ? null : (
        <div className="flex flex-wrap items-end gap-2">
          <div role="search" className="flex w-full min-w-0 sm:w-auto sm:min-w-64 sm:flex-1">
            <SearchInput
              aria-label="Search by credit note, invoice or customer"
              placeholder="Search credit note, invoice or customer"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          <OptionSelect
            aria-label="Status"
            className="w-full sm:w-44"
            value={search.status ?? ANY}
            onValueChange={(value) =>
              void navigate({ search: (prev) => parseCreditNoteSearch({ ...prev, status: value }) })
            }
            options={[
              { value: ANY, label: 'Any status' },
              ...CREDIT_NOTE_STATUSES.map((value) => ({
                value,
                label: `${label(value)} · ${data.items.filter((note) => note.status === value).length}`,
              })),
            ]}
          />
          {/* Beside the filters it clears, and only while one is on. */}
          {filtered ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clear}
              className="text-muted-foreground hover:text-foreground h-9 gap-1.5"
            >
              <XIcon className="size-4" />
              Clear filters
            </Button>
          ) : null}
        </div>
      )}

      <DataTable
        caption="Credit notes"
        title={`${filtered ? 'Matching credit notes' : 'All credit notes'} (${shown.length})`}
        // Only with rows: an empty table shows its empty state, and no line under it.
        footer={
          shown.length === 0 ? undefined : (
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={shown.length}
              onPageChange={setPage}
              ariaLabel="Pages of the credit notes"
            />
          )
        }
        columns={columns}
        data={paged.rows}
        getRowId={(note) => note.id}
        empty={
          data.items.length > 0 ? (
            <FilteredEmpty
              message={
                search.q === undefined
                  ? 'No credit notes in this status'
                  : `No credit notes matching “${search.q}”`
              }
              onClear={clear}
            />
          ) : (
            <EmptyState
              icon={<FileMinusIcon />}
              title="No credit notes yet"
              description="Open an issued or paid invoice and choose Credit note to reduce what it owes."
            />
          )
        }
      />

      <ConfirmDialog
        open={dialog?.kind === 'issue'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={`Issue ${name}?`}
        description={
          dialog === null
            ? ''
            : `${formatMoney(dialog.note.total, dialog.note.currency)} comes off what ${dialog.note.invoiceNumber ?? 'the invoice'} is owed. It cannot be edited after.`
        }
        confirmLabel="Issue credit note"
        busy={issue.isPending}
        onConfirm={() => {
          if (dialog !== null) issue.mutate(dialog.note.id, { onSuccess: close })
        }}
      />

      <NoteDialog
        open={dialog?.kind === 'void'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={`Void ${name}?`}
        description="The credit is withdrawn and the invoice owes that amount again."
        confirmLabel="Void credit note"
        noteLabel="Reason"
        required
        destructive
        busy={voidIt.isPending}
        onConfirm={(reason) => {
          if (dialog !== null) voidIt.mutate({ id: dialog.note.id, reason }, { onSuccess: close })
        }}
      />
    </div>
  )
}
